import { CREATE_AUDIT_EVENT } from "adapters/ehr.service";
import {
  END_ASYNC_RECORDING,
  GET_STT_TOKEN,
  PAUSE_RECORDING,
  RECORD_EVENTS,
  START_RECORDING,
} from "adapters/recognizer.service";
import { setEncounterIdInParams } from "config/helpers/encounterReferenceExtractor";
import { LIMITS } from "config/helpers/license_helper";
import { initializeSpeechService } from "config/helpers/speechTranscriptionHelpers/initialize_speech_helper";
import i18n from "multilingual";
import { toast } from "react-toastify";
import {
  leftPanelReset,
  rightPanelReset,
} from "redux/reducers/EncounterResetSlice";
import { recognizerManager } from "screens/patientInfo/classes/RecognizerManager";
import { getCurrentPlan } from "screens/store/storeHelper";
import { constants } from "../constants/redux-constants";
import { fetchUsage } from "./PlanUsageActions";
import { progressStatus } from "./loaderActions";
import { startWatchingTask } from "redux/redux-thunk/BackgroundtaskThunks";
import {
  addActiveSOAPGeneration,
  removeActiveSOAPGeneration,
} from "./activeSOAPGenerationActions";
import { encounterObject } from "./EncounterActions";
import { TASK_STATUS } from "config/constants/constants";
import { setConsent } from "./Consent";

var tokenRefreshTimeoutId = null;

export const initializeSttTokenService = (firstTime = true) => {
  return async (dispatch) => {
    try {
      const tokenResponse = await GET_STT_TOKEN();
      dispatch({
        type: constants.RECOGNIZER.SET_STT_TOKEN,
        payload: tokenResponse,
      });

      if (!firstTime) {
        recognizerManager.recognizer.authorizationToken = tokenResponse;
        recognizerManager.speechConfig.authorizationToken = tokenResponse;
      }

      tokenRefreshTimeoutId = setTimeout(() => {
        dispatch(initializeSttTokenService(false));
      }, 7 * 60 * 1000);
    } catch (error) {
      console.error("Error refreshing token:", error);
      return null;
    }
  };
};
export const startRecording = (payload) => {
  return async (dispatch, getState) => {
    let micPermissionStatus = null;
    const { patientObj } = getState().patientObj;
    const encounterObj = getState().encounterObj;
    let conversationAndEncounterIds = null;
    const handleError = (errorMessage) => {
      destroySttTokenTimeout();
      recognizerManager.stopTranscription();
      dispatch({
        type: constants.RECOGNIZER.HAS_ERROR_ENCOUNTERED,
        payload: {
          patientId: patientObj?.id,
          encounterId: encounterObj?.encounter?.id,
        },
      });
      dispatch({
        type: constants.RECOGNIZER.START_RECORDING_LOADING,
        payload: false,
      });
      if (errorMessage) {
        toast.error(errorMessage);
      }
    };

    try {
      dispatch(progressStatus(true));
      try {
        const permissions = await navigator.permissions.query({
          name: "microphone",
        });
        micPermissionStatus = permissions.state;
      } catch (error) {
        micPermissionStatus = "prompt";
      }
      if (micPermissionStatus === "denied") {
        handleError(i18n.t("error_messages.MIC_BLOCKED"));
        return;
      }
      if (micPermissionStatus !== "granted") {
        try {
          await navigator.mediaDevices.getUserMedia({ audio: true });
        } catch (error) {
          handleError(i18n.t("error_messages.ALLOW_MIC"));
          return;
        }
      }

      // 🔹 Step 2: Initialize STT service
      await dispatch(initializeSttTokenService()).catch(() => {
        throw new Error("Failed to initialize STT service");
      });

      const { token, language } = getState().recognizer;

      const code = language?.code ?? "en";

      if (!token) {
        handleError(i18n.t("error_messages.FAILED_INITIALIZE"));
        return;
      }

      // 🔹 Step 3: Initialize Speech Service
      initializeSpeechService(dispatch, payload);
      const currentPlan = getCurrentPlan();
      const planUsage = getState()?.planUsage?.usagePayload;

      const { practitionerData, organisationData, currentUserSettings } =
        getState().auth;

      const startRecordingPayload = {
        patientId: patientObj?.id,
        practitionerId: practitionerData?.id,
        organizationId: organisationData?.id,
        language: code,
        cdss:
          currentPlan?.toLowerCase() === "lite" ||
          !currentUserSettings?.display?.CDSSHidden ||
          LIMITS[currentPlan]?.cdss <= planUsage?.cdss
            ? false
            : true,
        ...(encounterObj?.encounter?.id && {
          encounterId: encounterObj.encounter.id,
        }),
      };

      try {
        // Fetch updated usage
        await dispatch(fetchUsage());
        conversationAndEncounterIds = await START_RECORDING(
          startRecordingPayload
        );
      } catch (error) {
        handleError("Failed to start recording. Please try again.");
        return;
      }

      if (!conversationAndEncounterIds?.id) {
        handleError("Failed to initialize recording session");
        return;
      }
      dispatch({
        type: constants.RECOGNIZER.START_RECORDING,
        payload: {
          patientId: patientObj?.id,
          encounterId: conversationAndEncounterIds?.encounterId,
          conversationId: conversationAndEncounterIds.id,
        },
      });
      if (conversationAndEncounterIds?.encounterId) {
        dispatch(
          encounterObject({
            id: conversationAndEncounterIds?.encounterId,
          })
        );
        dispatch(
          addActiveSOAPGeneration({
            patientId: patientObj?.id,
            encounterId: conversationAndEncounterIds?.encounterId,
            patientName: patientObj?.name?.[0]?.text,
          })
        );
      }
      setEncounterIdInParams(conversationAndEncounterIds?.encounterId);
      // dispatch(fetchUsage());
    } catch (error) {
      handleError("An error occurred while starting the recording");
      console.debug("Recording error:", error);
      dispatch(
        removeActiveSOAPGeneration({
          patientId: patientObj?.id,
          encounterId: conversationAndEncounterIds?.encounterId,
          patientName: patientObj?.name?.[0]?.text,
        })
      );
    } finally {
      dispatch(progressStatus(false));
    }
  };
};

export const pauseRecording = (payload) => {
  const { recognitionEvents, setRecognitionEvents, conversationId } = payload;

  return async (dispatch, getState) => {
    const state = getState();
    const patientId = state.patientObj?.patientObj?.id;
    const encounterId = state.encounterObj?.encounter?.id;
    dispatch({
      type: constants.RECOGNIZER.PAUSE_RECORDING,
      payload: { patientId, encounterId },
    });
    recognizerManager.pauseTranscription();
    destroySttTokenTimeout();

    let isRecordingPaused = await PAUSE_RECORDING({
      recognitionEvents,
      conversationId,
    });

    // if (isRecordingPaused) {
    setRecognitionEvents([]);
    return isRecordingPaused;
    // } else {
    // dispatch({
    //   type: constants.RECOGNIZER.HAS_ERROR_ENCOUNTERED,
    //   payload: true,
    // });
    // }
  };
};

export const resumeRecording = (payload) => {
  const {
    recognitionEvents,
    setRecognitionEvents,
    conversationId,
    handleTranscribed,
  } = payload;

  return async (dispatch, getState) => {
    const state = getState();
    const patientId = state.patientObj?.patientObj?.id;
    const encounterId = state.encounterObj?.encounter?.id;
    const handleError = (errorMessage) => {
      destroySttTokenTimeout();
      recognizerManager.stopTranscription();
      dispatch({
        type: constants.RECOGNIZER.HAS_ERROR_ENCOUNTERED,
        payload: { patientId, encounterId },
      });
      dispatch({
        type: constants.RECOGNIZER.START_RECORDING_LOADING,
        payload: false,
      });
      if (errorMessage) {
        toast.error(errorMessage);
      }
    };
    let micPermissionStatus = null;
    try {
      const permissions = await navigator.permissions.query({
        name: "microphone",
      });
      micPermissionStatus = permissions.state;
    } catch (error) {
      micPermissionStatus = "prompt";
    }
    if (micPermissionStatus === "denied") {
      handleError(i18n.t("error_messages.MIC_BLOCKED"));
      return;
    }
    if (micPermissionStatus !== "granted") {
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (error) {
        handleError(i18n.t("error_messages.ALLOW_MIC"));
        return;
      }
    }
    await dispatch(
      initializeSttTokenService(typeof handleTranscribed === "function")
    );

    //If transcribed event are recieved, need to initialize the speech recognizer manager
    if (typeof handleTranscribed === "function")
      initializeSpeechService(dispatch, payload);

    let isRecordingResumed = await RECORD_EVENTS({
      recognitionEvents,
      conversationId,
    });

    // if (isRecordingResumed === "duration_limit_exceeded") {
    //   dispatch(
    //     stopRecording({
    //       conversationId,
    //       recognitionEvents,
    //       setRecognitionEvents,
    //     })
    //   );
    //   return false;
    // }

    if (isRecordingResumed) {
      dispatch({
        type: constants.RECOGNIZER.RESUME_RECORDING,
        payload: { patientId, encounterId },
      });

      recognizerManager.resumeTranscription();
      return isRecordingResumed;
    } else {
      dispatch({
        type: constants.RECOGNIZER.HAS_ERROR_ENCOUNTERED,
        payload: { patientId, encounterId },
      });
    }
  };
};

export const recordEvents = (payload) => {
  const { recognitionEvents, conversationId } = payload;

  return async (dispatch, getState) => {
    let isRecordingResumed = await RECORD_EVENTS({
      recognitionEvents,
      conversationId,
    });

    const state = getState();
    const patientId = state.patientObj?.patientObj?.id;
    const encounterId = state.encounterObj?.encounter?.id;

    if (isRecordingResumed === "duration_limit_exceeded") {
      toast.warning("conversation duration exceeds the allowed limit");
      dispatch(
        stopRecording({
          conversationId,
          recognitionEvents,
          setRecognitionEvents: () => {},
        })
      );
      return false;
    }

    if (!isRecordingResumed?.cdss) {
      dispatch({
        type: constants.RECOGNIZER.HAS_ERROR_ENCOUNTERED,
        payload: {
          patientId,
          encounterId,
          error: isRecordingResumed?.cdss,
        },
      });
    } else {
      dispatch({
        type: constants.RECOGNIZER.UPDATE_CDSS_DATA,
        payload: isRecordingResumed?.cdss,
      });
      if (isRecordingResumed?.cdss?.titleCode) {
        dispatch({
          type: constants.RECOGNIZER.SET_CDSS_TITLE,
          payload: isRecordingResumed?.cdss?.titleCode,
        });
      }
    }
  };
};

export const destroySttTokenTimeout = () => {
  if (tokenRefreshTimeoutId) {
    clearTimeout(tokenRefreshTimeoutId);
    tokenRefreshTimeoutId = null;
  }
};

export const refreshPatientHistoryWindow = ({
  setRecognitionEvents,
  dispatch,
}) => {
  dispatch(leftPanelReset());
  dispatch(rightPanelReset());
  if (typeof setRecognitionEvents === "function") {
    setRecognitionEvents([]);
  }
  dispatch({
    type: constants.RECOGNIZER.START_RECORDING_LOADING,
    payload: false,
  });
};

export const refreshRecognitionEventAndState = ({
  setRecognitionEvents,
  dispatch,
}) => {
  setRecognitionEvents([]);
  dispatch({
    type: constants.RECOGNIZER.START_RECORDING_LOADING,
    payload: false,
  });
};

export const stopRecording = (payload) => {
  debugger;
  const { recognitionEvents, setRecognitionEvents, conversationId, context } =
    payload;
  return async (dispatch, getState) => {
    const reduxState = getState();
    const PractitionerID = reduxState?.auth?.practitionerData?.id;
    const patientObj = reduxState?.patientObj?.patientObj;
    const encounterObj = reduxState?.encounterObj?.encounter;

    try {
      dispatch({
        type: constants.RECOGNIZER.CONVERSATION_ENDING,
      });
      recognizerManager.stopTranscription();
      destroySttTokenTimeout();
      // dispatch({
      //   type: constants.PROGRESS_STATUS,
      //   payload: true,
      // });

      let response = await END_ASYNC_RECORDING(
        {
          recognitionEvents,
          conversationId,
          context,
          patientObj,
          encounterObj,
        },
        dispatch
      );

      let isRecordingEnded = response.status >= 200 && response.status <= 206;
      let taskId = response?.data?.taskId;

      if (isRecordingEnded && taskId) {
        dispatch(
          startWatchingTask(
            taskId,
            "soap",
            // PractitionerID,
            setRecognitionEvents
          )
        );
      } else {
        dispatch(
          resetProgressState({
            patientId: patientObj?.id,
            encounterId: encounterObj?.id,
          })
        );
        dispatch({
          type: constants.RECOGNIZER.STOP_RECORDING,
          payload: { patientId: patientObj?.id, encounterId: encounterObj?.id },
        });
        dispatch({
          type: constants.RECOGNIZER.RESET_RECOGNIZER_FOR_ENCOUNTER,
        });
        // dispatch({
        //   type: constants.PROGRESS_STATUS,
        //   payload: false,
        // });
      }
      refreshPatientHistoryWindow({
        setRecognitionEvents,
        dispatch,
      });
      removeActiveSOAPGeneration({
        patientId: patientObj?.id,
        encounterId: encounterObj?.id,
      });
    } catch (error) {
      debugger;
      if (error?.message) {
        // toast.error(error?.message);
        console.log("44Error stopping recording:", error);
      } else if (typeof error === "string") {
        console.log("44Error stopping recording:", error);
        // toast.error(error);
      }
      dispatch(
        resetProgressState({
          patientId: patientObj?.id,
          encounterId: encounterObj?.id,
        })
      );
      dispatch({
        type: constants.RECOGNIZER.STOP_RECORDING,
        payload: { patientId: patientObj?.id, encounterId: encounterObj?.id },
      });
      dispatch({
        type: constants.RECOGNIZER.RESET_RECOGNIZER_FOR_ENCOUNTER,
      });
      // dispatch({
      //   type: constants.PROGRESS_STATUS,
      //   payload: false,
      // });
      refreshPatientHistoryWindow({
        setRecognitionEvents,
        dispatch,
      });
      removeActiveSOAPGeneration({
        patientId: patientObj?.id,
        encounterId: encounterObj?.id,
      });
    } finally {
      dispatch({
        type: constants.RECOGNIZER.START_RECORDING_LOADING,
        payload: false,
      });
    }
  };
};

export const saveDraft = (payload) => {
  const { recognitionEvents, setRecognitionEvents, conversationId } = payload;
  debugger;

  return async (dispatch, getState) => {
    debugger;
    let isSavedTranscript;
    // TODO once data save with record events then change pause_recording to record_events
    const reduxState = getState();
    const PractitionerID = reduxState?.auth?.practitionerData?.id;
    const patientObj = reduxState?.patientObj?.patientObj;
    const encounterObj = reduxState?.encounterObj?.encounter;
    const PatientName = patientObj?.name?.[0]?.text;
    const key = `${patientObj?.id}-${encounterObj?.id}`;
    const paused = reduxState?.recognizer?.recordingByEncounter?.[key]?.paused;
    if (conversationId && !paused) {
      isSavedTranscript = await PAUSE_RECORDING({
        recognitionEvents,
        conversationId,
      });
    }

    recognizerManager.stopTranscription();
    destroySttTokenTimeout();

    dispatch({
      type: constants.RECOGNIZER.RESET_RECOGNIZER_FOR_ENCOUNTER,
    });
    dispatch({
      type: constants.RECOGNIZER.STOP_RECORDING,
      payload: {
        patientId: patientObj?.id,
        encounterId: encounterObj?.id,
      },
    });
    dispatch({
      type: constants.RECOGNIZER.RESET_PROGRESS_STATE,
      payload: {
        patientId: patientObj?.id,
        encounterId: encounterObj?.id,
      },
    });
    dispatch(
      removeActiveSOAPGeneration({
        patientId: patientObj?.id,
        encounterId: encounterObj?.id,
      })
    );
    dispatch(setConsent(false));

    CREATE_AUDIT_EVENT({
      practitionerID: `Practitioner/${PractitionerID}`,
      patientName: PatientName,
      type: { display: "Create", code: "110120" },
      reason: JSON.stringify({ msg_code: "DRAFT_ADDED" }),
    });
  };
};

export const autoStartRecording = (payload) => ({
  type: constants.RECOGNIZER.AUTO_START,
  payload: payload,
});
export const zoomCallOngoingChange = (payload) => ({
  type: constants.RECOGNIZER.ZOOM_CALL_ONGOING,
  payload: payload,
});

export const updateProgressState = (payload) => ({
  type: constants.RECOGNIZER.UPDATE_PROGRESS_STATE,
  payload: payload,
});
export const resetProgressState = ({ patientId, encounterId }) => ({
  type: constants.RECOGNIZER.RESET_PROGRESS_STATE,
  payload: { patientId, encounterId },
});
